import type { Item } from "@/lib/types";
import {
  getLocationTier,
  getUserStateAndLGA,
  type LocationTier,
  type UserLocationForHierarchy,
} from "@/lib/locationHierarchy";
import { isSellerAvailabilityConfirmed } from "@/lib/items/sellerAvailabilityConfirmed";
import {
  filterItemsByDiscoveryGranularity,
  type HomepageDiscoveryGranularity,
  resolveEffectiveDiscoveryGranularity,
} from "@/lib/items/homepageDiscoveryGranularity";

export const RECENTLY_CONFIRMED_SHELF_TITLE = "Still available near you";
export const RECENTLY_CONFIRMED_SHELF_SUBTITLE = "Availability reconfirmed";

export const RECENTLY_CONFIRMED_SHELF_MIN_ITEMS = 3;
export const RECENTLY_CONFIRMED_SHELF_MAX_ITEMS = 12;

export const RECENTLY_CONFIRMED_QUERY_POOL_LIMIT = 48;

export type RecentlyConfirmedNearYouOptions = {
  userLocation: UserLocationForHierarchy | null;
  includeOtherLocationTier?: boolean;
  windowDays?: number;
  enabled?: boolean;
  now?: Date;
  discoveryGranularity?: HomepageDiscoveryGranularity;
  discoveryAutoWiden?: boolean;
};

function getTierForItem(
  item: Item,
  userLocation: UserLocationForHierarchy,
): LocationTier {
  const { state: userState, lga: userLGA } = getUserStateAndLGA(userLocation);
  const areaName = userLocation.areaName ?? "";
  return getLocationTier(item.location ?? "", areaName, userState, userLGA);
}

function getConfirmedItems(
  items: Item[],
  options: RecentlyConfirmedNearYouOptions,
): Item[] {
  return items.filter((item) =>
    isSellerAvailabilityConfirmed(item, {
      enabled: options.enabled !== false,
      windowDays: options.windowDays,
      now: options.now,
    }),
  );
}

/**
 * Filters confirmed-within-window items and orders by location tier, then
 * most-recent seller confirmation within each tier.
 */
export function sortRecentlyConfirmedNearYou(
  items: Item[],
  options: RecentlyConfirmedNearYouOptions,
): Item[] {
  if (!options.userLocation?.areaName?.trim()) {
    return [];
  }

  const confirmed = getConfirmedItems(items, options);
  const configuredGranularity = options.discoveryGranularity ?? "lga";
  const reachFiltered = filterItemsByDiscoveryGranularity(
    confirmed,
    options.userLocation,
    configuredGranularity,
    options.includeOtherLocationTier === true,
  );

  const byTier: Record<LocationTier, Item[]> = {
    area: [],
    lga: [],
    state: [],
    other: [],
  };

  for (const item of reachFiltered) {
    const tier = getTierForItem(item, options.userLocation);
    if (tier === "other" && options.includeOtherLocationTier === false) {
      continue;
    }
    byTier[tier].push(item);
  }

  const sortByConfirmedAt = (a: Item, b: Item) => {
    const aTime = a.sellerAvailabilityConfirmedAt?.getTime() ?? 0;
    const bTime = b.sellerAvailabilityConfirmedAt?.getTime() ?? 0;
    return bTime - aTime;
  };

  const tiers: LocationTier[] = ["area", "lga", "state", "other"];
  const ordered: Item[] = [];
  for (const tier of tiers) {
    byTier[tier].sort(sortByConfirmedAt);
    ordered.push(...byTier[tier]);
  }

  return ordered.slice(0, RECENTLY_CONFIRMED_SHELF_MAX_ITEMS);
}

export function shouldShowRecentlyConfirmedShelf(
  items: Item[],
  options: RecentlyConfirmedNearYouOptions,
): boolean {
  if (options.enabled === false) {
    return false;
  }
  if (!options.userLocation?.areaName?.trim()) {
    return false;
  }
  const sorted = prepareRecentlyConfirmedShelfItems(items, options);
  return sorted.length > 0;
}

export function prepareRecentlyConfirmedShelfItems(
  items: Item[],
  options: RecentlyConfirmedNearYouOptions,
): Item[] {
  if (options.enabled === false || !options.userLocation?.areaName?.trim()) {
    return [];
  }

  const confirmed = getConfirmedItems(items, options);
  const configuredGranularity = options.discoveryGranularity ?? "lga";
  const effectiveGranularity = resolveEffectiveDiscoveryGranularity(
    confirmed,
    options.userLocation,
    {
      configured: configuredGranularity,
      autoWiden: options.discoveryAutoWiden !== false,
      minCount: RECENTLY_CONFIRMED_SHELF_MIN_ITEMS,
      includeOtherLocationTier: options.includeOtherLocationTier === true,
    },
  );

  const reachFiltered = filterItemsByDiscoveryGranularity(
    confirmed,
    options.userLocation,
    effectiveGranularity,
    options.includeOtherLocationTier === true,
  );

  return finalizeShelfItems(
    sortRecentlyConfirmedNearYou(reachFiltered, {
      ...options,
      discoveryGranularity: effectiveGranularity,
      discoveryAutoWiden: false,
    }),
  );
}

function finalizeShelfItems(sorted: Item[]): Item[] {
  if (sorted.length < RECENTLY_CONFIRMED_SHELF_MIN_ITEMS) {
    return [];
  }
  return sorted;
}
